import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { Authorizations } from "~/api/types";
import { useAuth } from "~/contexts/AuthProvider.tsx";
import { canCollectionMember } from "~/helpers/authorizations";

import { Invitation, Membership } from "../../api/Api";
import DynamicRowTooltip from "../ui/dynamicRowTooltip/DynamicRowTooltip";
import { Pagination, PaginationConfig } from "../ui/pagination/Pagination";
import DeleteTeamMemberPopover from "./DeleteTeamMemberPopover";
import { EditTeamMemberModal } from "./EditTeamMemeberModal";
import styles from "./TeamMembersTable.module.scss";

type TeamMembersTableProps =
  | {
      type: "active";
      data: Membership[];
      isLoading: boolean;
      pagination: PaginationConfig;
      setPagination: (p: PaginationConfig) => void;
      authorizations?: Authorizations;
    }
  | {
      type: "pending";
      data: Invitation[];
      isLoading: boolean;
      pagination: PaginationConfig;
      setPagination: (p: PaginationConfig) => void;
      authorizations?: Authorizations;
    };

function getWhiteLabelData(
  row: Membership | Invitation,
  type: "active" | "pending",
) {
  if (type === "active") {
    const membership = row as Membership;

    return {
      name: membership.user?.tenant_account?.name,
      logo: membership.user?.tenant_account?.logo,
    };
  }

  const invitation = row as Invitation;

  if (!invitation.tenant_account_id) {
    return { name: undefined, logo: undefined };
  }

  return {
    name: invitation.team_name,
    logo: invitation.team_logo,
  };
}

const isPermittedAction = (
  rowIndex: number,
  authorizations: Authorizations | undefined,
  action: string,
) => canCollectionMember(authorizations, rowIndex, action);

const columnHelper = createColumnHelper<Invitation | Membership>();

export const TeamMembersTable = ({
  data,
  isLoading,
  pagination,
  setPagination,
  type,
  authorizations,
}: TeamMembersTableProps) => {
  const { user } = useAuth();

  const [editableMembership, setEditableMembership] =
    useState<Membership | null>(null);

  const hasWhiteLabel = useMemo(
    () =>
      data.some((row) => {
        const wl = getWhiteLabelData(row, type);

        return wl.name && wl.logo;
      }),
    [data, type],
  );

  const whiteLabelColumn = columnHelper.accessor(
    (row) => getWhiteLabelData(row, type),
    {
      id: "whiteLabel",
      header: "White Label",
      cell: (info) => {
        if (isLoading) return <Skeleton />;

        const { name, logo } = info.getValue();

        if (!name || !logo) return null;

        return (
          <div className={styles.whiteLabelColumn}>
            <img src={logo["2x"]} alt={`${name} Logo`} />
            <div>{name}</div>
          </div>
        );
      },
    },
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => {
          if (type === "active" && "user" in row) {
            return `${row.user.first_name} ${row.user.last_name}`;
          } else if ("email" in row) {
            return row.email;
          }

          return "";
        },
        {
          id: "name",
          header: "Member",
          cell: (info) => {
            if (isLoading) return <Skeleton width={100} />;

            // @ts-expect-error TS2339 non-existing className
            return <div className={styles.member}>{info.getValue()}</div>;
          },
        },
      ),
      ...(type === "active"
        ? [
            columnHelper.accessor(
              (row) => {
                if ("user" in row) {
                  return row.user.email;
                }

                return "";
              },
              {
                id: "email",
                header: "Email",
                cell: (info) => {
                  if (isLoading) return <Skeleton />;

                  return info.getValue();
                },
              },
            ),
          ]
        : []),
      columnHelper.accessor("admin", {
        id: "role",
        header: "Role",
        cell: (info) => {
          if (isLoading) return <Skeleton />;

          return info.getValue() ? "Admin" : "Member";
        },
      }),
      columnHelper.accessor("site_names", {
        id: "access",
        header: "Access",
        cell: (info) => {
          const row = info.row.original;
          if (isLoading) return <Skeleton />;
          if (row.admin)
            return <span className={styles.siteTagGreen}>All Sites</span>;
          if (!row.admin && !row.site_names?.length)
            return <span className={styles.siteTagGreen}> All Sites</span>;
          const sites = info.getValue() as string[];

          return (
            <div className={styles.sites}>
              {sites?.length === 0 && (
                <span className={styles.siteTagGreen}> All Sites</span>
              )}
              {sites?.length > 0 && <DynamicRowTooltip items={sites} />}
            </div>
          );
        },
      }),
      ...(type === "pending"
        ? [
            columnHelper.accessor("created_at", {
              id: "created_at",
              header: "Sending date",
              cell: (info) => {
                if (isLoading) return <Skeleton width={100} />;

                return new Date(
                  info.getValue() as string | number,
                ).toLocaleDateString();
              },
            }),
          ]
        : []),
      ...(hasWhiteLabel ? [whiteLabelColumn] : []),
      columnHelper.accessor((row) => row.id, {
        id: "actions",
        header: "",
        size: 50,
        cell: (info) => {
          const row = info.row.original;
          const rowIndex = info.row.index;
          const memberName =
            type === "active" && "user" in row
              ? `${row.user.first_name} ${row.user.last_name}`
              : "email" in row
                ? row.email
                : undefined;
          const isSelf =
            type === "active" &&
            user &&
            "user" in row &&
            row.user.id === user.id;

          return (
            <DeleteTeamMemberPopover
              id={row.id}
              type={type}
              memberName={memberName}
              canDestroy={isPermittedAction(
                rowIndex,
                authorizations,
                "destroy",
              )}
              canPromote={isPermittedAction(
                rowIndex,
                authorizations,
                "promote",
              )}
              canDemote={isPermittedAction(rowIndex, authorizations, "demote")}
              canResend={isPermittedAction(rowIndex, authorizations, "resend")}
              isSelf={isSelf}
              isChangeRoleDisabled={!!editableMembership}
              setEditableMembership={() => {
                if (type === "active") {
                  setEditableMembership(row as Membership);
                }
              }}
            />
          );
        },
      }),
    ],
    [
      isLoading,
      type,
      authorizations,
      user,
      hasWhiteLabel,
      whiteLabelColumn,
      editableMembership,
    ],
  );

  const table = useReactTable<Invitation | Membership>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderSkeletonRows = (numRows: number) => {
    return Array.from({ length: numRows }).map((_, rowIndex) => (
      <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
        {Array.from({ length: columns.length }).map((_, colIndex) => (
          <td
            key={`skeleton-cell-${rowIndex}-${colIndex}`}
            className="px-6 py-4 whitespace-nowrap"
          >
            <Skeleton className="h-6 w-full" />
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <>
      <div className={styles.teamMembers}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={
                      header.column.columnDef.size
                        ? { width: header.column.columnDef.size }
                        : undefined
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading
              ? renderSkeletonRows(10)
              : table.getRowModel().rows.map((row, rowIndex) => (
                  <tr
                    key={`row-${type}-${row.original.id ?? `index-${rowIndex}`}`}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <td
                        key={`cell-${type}-${row.original.id ?? `index-${rowIndex}`}-${cell.column.id}-${cellIndex}`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <Pagination
        {...pagination}
        onPageChange={(page) => {
          setPagination({ ...pagination, currentPage: page });
        }}
        onItemsPerPageChange={(items) => {
          setPagination({ ...pagination, itemsPerPage: items });
        }}
      />
      <EditTeamMemberModal
        membership={editableMembership}
        closeModal={() => setEditableMembership(null)}
        modalLabel={<h3 className={styles.modalTitle}>Change User Role</h3>}
      />
    </>
  );
};
